import { useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import ListItemIcon from '@mui/material/ListItemIcon'
import LogoutIcon from '@mui/icons-material/Logout'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined'
import { useAuth } from '../contexts/AuthContext'
import { useCondominium } from '../contexts/CondominioContext'
import { MenuTwoTone, Person2Outlined } from '@mui/icons-material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import indlusLogo from '../../assets/indlusr.png'
import LanguageSwitcher from './LanguageSwitcher'
import { buildSubdomainUrl } from '../../config'

// Altura fixa da AppBar — exportada porque AppLayout (espaçador) e Sidebar
// (posição/altura do drawer, botão de colapsar) têm de acompanhar este valor
// em vez de repetirem o número, tal como já acontece com SIDEBAR_WIDTH.
export const NAVBAR_HEIGHT = 60

function getInitials(name?: string | null) {
    if (!name) return ''
    return name
        .split(' ')
        .map((part) => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
}

interface Props {
    onMenuClick?: () => void
    showMenuButton?: boolean
    /** Estado do drawer móvel — troca o ícone para "fechar" enquanto estiver aberto. */
    mobileOpen?: boolean
}

export default function Navbar({ onMenuClick, showMenuButton = false, mobileOpen = false }: Props) {
    const { t } = useTranslation('nav')
    const { user, isAuthenticated, logout, exitEstate, hasPermission, hasAnyPermission } = useAuth()
    const { activeCondominium, clearActiveCondominium } = useCondominium()
    // Exit (largar o estate activo) mantém-se exclusivo do ADMIN.
    const isAdmin = hasPermission('ESTATE_CONTEXT_EXIT')
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const menuOpen = Boolean(anchorEl)

    const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleCloseMenu = () => setAnchorEl(null)

    const handleLogout = async () => {
        handleCloseMenu()
        // Espera o servidor expirar o cookie de sessão ANTES de navegar — um
        // redirect imediato cancelaria o pedido e a sessão continuaria válida.
        await logout()
        // `replace` (não `href =`) para o ecrã autenticado NÃO ficar no
        // histórico — senão o botão "Retroceder" do browser reabre-o a partir
        // da bfcache, com o estado antigo em memória, e parece que o logout
        // não fez nada. Navegação de browser real para o www — navigate('/login')
        // deixaria o utilizador no /login do subdomínio actual (cond/prop).
        window.location.replace(buildSubdomainUrl('www', '/login'))
    }

    const handleGoHome = async (event: MouseEvent<HTMLElement>) => {
        event.preventDefault()
        if (activeCondominium) {
            clearActiveCondominium()
            if (isAdmin) await exitEstate()  // ← liberta o estate activo do token JWT, tal como o botão "Mudar"
        }
        navigate('/')
    }

    return (

        isAuthenticated ? (
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid #E5E7EB',
                    color: 'text.primary',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <Toolbar sx={{ px: { xs: 2, sm: 3 }, gap: 1.5, minHeight: `${NAVBAR_HEIGHT}px !important` }}>
                    {/* Logo / Brand */}
                    {showMenuButton && (
                        <IconButton
                            onClick={onMenuClick}
                            edge="start"
                            sx={{ display: { md: 'none' }, mr: 0.5 }}
                            aria-label={mobileOpen ? t('navbar.closeMenu') : t('navbar.openMenu')}
                        >
                            {mobileOpen ? <CloseRoundedIcon /> : <MenuTwoTone />}
                        </IconButton>
                    )}
                    <Box
                        component={RouterLink}
                        // to={isAuthenticated ? '/dashboard' : '/login'}
                        to={'/'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            textDecoration: 'none',
                            color: 'inherit',
                        }}
                        onClick={handleGoHome}
                    >
                        <Box
                            component="img"
                            src={indlusLogo}
                            alt="Indlus"
                            sx={{ height: 46, width: 'auto', display: 'block', flexShrink: 0 }}
                        />
                    </Box>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Right side: auth-dependent content */}

                    <>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: { xs: 'none', md: 'block' }, mr: 0.5 }}
                        >
                            {/* {user.companyName} */}
                        </Typography>

                        <LanguageSwitcher />

                        <IconButton onClick={handleOpenMenu} size="small" sx={{ ml: 0.5 }}>
                            <Avatar
                                sx={{
                                    width: 34,
                                    height: 34,
                                    bgcolor: 'primary.light',
                                    fontSize: '0.8125rem',
                                    fontWeight: 600,
                                }}
                            >
                                {getInitials(user?.nome || user?.email)}
                            </Avatar>
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={menuOpen}
                            onClose={handleCloseMenu}
                            onClick={handleCloseMenu}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        // PaperProps={{
                        //     elevation: 2,
                        //     sx: { mt: 1, minWidth: 220, borderRadius: 2 },
                        // }}
                        >
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="body2" noWrap>
                                    {/* {user.name} */}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {/* {user.email} */}
                                </Typography>
                            </Box>

                            <Divider />

                            <MenuItem
                                component={RouterLink}
                                to={activeCondominium ? `/properties/${activeCondominium.id}` : '/'}
                            >
                                <ListItemIcon>
                                    <DashboardOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                {t('navbar.dashboard')}
                            </MenuItem>

                            <MenuItem component={RouterLink} to="/profile">
                                <ListItemIcon>
                                    <Person2Outlined fontSize="small" />
                                </ListItemIcon>
                                {t('navbar.myProfile')}
                            </MenuItem>

                            {hasPermission('TENANT_MANAGE') && (
                                <MenuItem component={RouterLink} to="/modules">
                                    <ListItemIcon>
                                        <AppsOutlinedIcon fontSize="small" />
                                    </ListItemIcon>
                                    {t('navbar.modules')}
                                </MenuItem>
                            )}

                            {hasAnyPermission('TENANT_MANAGE', 'TENANT_EDITAR') && (
                                <MenuItem component={RouterLink} to="/company">
                                    <ListItemIcon>
                                        <ApartmentOutlinedIcon fontSize="small" />
                                    </ListItemIcon>
                                    {t('navbar.company')}
                                </MenuItem>
                            )}

                            <MenuItem component={RouterLink} to="/support">
                                <ListItemIcon>
                                    <SupportAgentOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                {t('navbar.support')}
                            </MenuItem>

                            {(hasPermission('TENANT_MANAGE') || hasPermission('ROLES_MANAGE')) && (
                                <MenuItem component={RouterLink} to="/settings">
                                    <ListItemIcon>
                                        <SettingsOutlinedIcon fontSize="small" />
                                    </ListItemIcon>
                                    {t('navbar.settings')}
                                </MenuItem>
                            )}

                            <Divider />

                            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                                </ListItemIcon>
                                {t('navbar.logout')}
                            </MenuItem>
                        </Menu>
                    </>

                </Toolbar>
            </AppBar>
        ) : (
            <></>
        )

    )
}
